import { yupResolver } from '@hookform/resolvers/yup';
import React, {
  createContext,
  useState,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  SetStateAction,
  Dispatch,
  useMemo,
} from 'react';
import {
  Control,
  FieldValues,
  FormState,
  useForm,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetError,
  UseFormSetFocus,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { schema } from './validations/FormDataValidation';
import { schemaFormFinanceiro } from '~/pages/EmissaoNFE/validations/FormFinanceiroValidation';
import { schemaFormItens } from '~/pages/EmissaoNFE/validations/FormItensValidation';

import {
  Loja,
  CuponsReferenciados,
  ParametrosLoja,
  Produto,
  SubTotais,
  ParcelaFinanceiro,
  LogProps,
  ListaDeCFOP,
  LogFilesProps,
} from './protocols';
import { CalculaTotais } from './functions/Calculos';
import useAuth from '~/hooks/useAuth';

import nfe from './defaultData/nfe.json';
import { insertOrUpdate, MasterDetailProps } from '~/utils/masterDetail';
import api from '~/services/api';
import { AgendamentosProps } from '~/components/VincularAgendamento/types';
import { AgendamentoSelecionadosStorage } from '~/components/VincularAgendamento/class/AgendamentoSelecionadosStorage';
import { toNumberOrZero } from '~/utils/functions';

type FormType = {
  register: UseFormRegister<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  control: Control<FieldValues, any>;
  reset: UseFormReset<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  formState: FormState<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setError: UseFormSetError<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  setFocus: UseFormSetFocus<FieldValues>;
};

type EmissaoNFEContextData = {
  isUpdate: boolean;
  setIsUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  codLoja: number;
  loja: Loja;
  parceiro: any;
  setCodLoja: React.Dispatch<React.SetStateAction<number>>;
  changeLoja: (data: Loja) => void;
  changeParceiro: (data: any) => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  currentTab: string;
  formNFE: FormType;
  formItens: FormType;
  formFinanceiro: FormType;
  formDadosEntrega: FormType;
  setCuponsReferenciados: React.Dispatch<
    React.SetStateAction<CuponsReferenciados[]>
  >;
  cuponsReferenciados: CuponsReferenciados[];
  setParametrosLoja: React.Dispatch<React.SetStateAction<ParametrosLoja>>;
  parametrosLoja: ParametrosLoja;
  produtos: Produto[];
  setProdutos: React.Dispatch<React.SetStateAction<Produto[]>>;
  produtoSelecionado: Produto;
  setProdutoSelecionado: React.Dispatch<React.SetStateAction<Produto>>;
  subTotais: SubTotais;
  setSubTotais: React.Dispatch<React.SetStateAction<SubTotais>>;

  // FINANCEIRO
  parcelasFinanceiro: ParcelaFinanceiro[];
  setParcelasFinanceiro: React.Dispatch<
    React.SetStateAction<ParcelaFinanceiro[]>
  >;
  parcelaSelecionada: ParcelaFinanceiro;
  setParcelaSelecionada: React.Dispatch<
    React.SetStateAction<ParcelaFinanceiro>
  >;
  isFormFinanceiroEditing: any;
  setIsFormFinanceiroEditing: any;

  logs: LogProps[];
  setLogs: React.Dispatch<React.SetStateAction<LogProps[]>>;
  isBonificado: boolean;
  setIsBonificado: React.Dispatch<React.SetStateAction<boolean>>;
  totalFinanceiro: number;
  setTotalFinanceiro: React.Dispatch<React.SetStateAction<number>>;
  resetFormData: () => void;
  isFormItendEditing: any;
  setIsFormItendEditing: React.Dispatch<React.SetStateAction<any>>;
  listaDeCfop: ListaDeCFOP[];
  setListaDeCfop: React.Dispatch<React.SetStateAction<ListaDeCFOP[]>>;
  setExisteLogAcbr: React.Dispatch<React.SetStateAction<LogFilesProps>>;
  existeLogAcbr: LogFilesProps;
  masterDetail: MasterDetailProps[];
  setMasterDetail: Dispatch<SetStateAction<MasterDetailProps[]>>;
  nfManutencao: boolean;
  setNfManutencao: React.Dispatch<React.SetStateAction<boolean>>;
  setNfCancelada: React.Dispatch<React.SetStateAction<boolean>>;
  nfCancelada: boolean;
  isDisabled: boolean;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;

  // Modal and Pedido related
  isModalOpen: any;
  onCloseModal: () => void;
  handleChangeOpenModalFunction: (isOpen: boolean) => void;
  produtosTable: any[];
  finalizadorasTable: any[];
  handlePedidosPendentes: (pedidoPendente: any[]) => void;
  handleShowTablePedidosPendentes: (
    showPedido: boolean,
    showItem: boolean,
  ) => void;
  showTablePedidosPendentes: { pedido: boolean; item: boolean };
  pedidoPendenteSelected: any | null;
  handleAddProdutoTable: (produto: any) => void;
  handlePedido: (item: any | any[]) => void;
  handleAddFinalizadoraTable: (finalizadora: any | any[]) => void;
  handleAddFinalizadoraPedido: (finalizadoraItem: any | any[]) => void;
  foirmImportarPedido: any;
  formTabFinalizacao: any;
  onOpenModal: (data: any) => void;
  pedidosPendentes: any;
  handleItensPedidosPendentes: (data: any) => void;
  handlePedidoPendenteSelected: (data: any) => void;
  itensPedidoPendente: any;
  pedido: any;
  setItensPedidoPendente: React.Dispatch<React.SetStateAction<any[]>>;
  setPedidoPententeSelected: React.Dispatch<React.SetStateAction<any[]>>;

  qtdAgendamento: {
    pagar: number;
    receber: number;
  };
  setQtdAgendamento: React.Dispatch<
    React.SetStateAction<{
      pagar: number;
      receber: number;
    }>
  >;
  agendamentos: AgendamentosProps[];
  setAgendamentos: React.Dispatch<React.SetStateAction<AgendamentosProps[]>>;
  tipoOperacao: number | undefined;
  setTipoOperacao: React.Dispatch<React.SetStateAction<number | undefined>>;
  localConsumoDesabilitado: boolean;
  setLocalConsumoDesabilitado: React.Dispatch<React.SetStateAction<boolean>>;
  codCidadeEntregaRetriever: number | undefined;
  setCodCidadeEntregaRetriever: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  codIbsCbsRetriever: number | null | undefined;
  setCodIbsCbsRetriever: React.Dispatch<
    React.SetStateAction<number | null | undefined>
  >;
};

export const EmissaoNFEContext = createContext({} as EmissaoNFEContextData);

interface EmissaoNFEContextProviderProps {
  children: ReactNode;
}

export function EmissaoNFEContextProvider({
  children,
}: EmissaoNFEContextProviderProps): JSX.Element {
  const { user } = useAuth();
  const [isUpdate, setIsUpdate] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>('nfe');
  const [loja, setLoja] = useState({} as Loja);
  const [codLoja, setCodLoja] = useState<number>(0);
  const [parceiro, setParceiro] = useState({} as any);
  const [cuponsReferenciados, setCuponsReferenciados] = useState<
    CuponsReferenciados[]
  >([]);
  const [parametrosLoja, setParametrosLoja] = useState<ParametrosLoja>(
    {} as ParametrosLoja,
  );
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto>(
    {} as Produto,
  );
  const [subTotais, setSubTotais] = useState<SubTotais>({
    totalNF: 0,
    totalBC: 0,
    totalICMS: 0,
    totBcIbsCbs: 0,
    totCbs: 0,
    totIbsEst: 0,
    totIbsMun: 0,
  });

  // FINANCEIRO
  const [parcelasFinanceiro, setParcelasFinanceiro] = useState<
    ParcelaFinanceiro[]
  >([]);
  const [isFormFinanceiroEditing, setIsFormFinanceiroEditing] = useState({
    isEdit: false,
    uuid: null,
  });
  const [codIbsCbsRetriever, setCodIbsCbsRetriever] = useState<
    number | null | undefined
  >(null);

  const [parcelaSelecionada, setParcelaSelecionada] =
    useState<ParcelaFinanceiro>({} as ParcelaFinanceiro);
  const [logs, setLogs] = useState<LogProps[]>([]);
  const [isBonificado, setIsBonificado] = useState(false);
  const [totalFinanceiro, setTotalFinanceiro] = useState(0);
  const [isFormItendEditing, setIsFormItendEditing] = useState({
    isEdit: false,
    uuid: null,
  });
  const [listaDeCfop, setListaDeCfop] = useState<ListaDeCFOP[]>([]);
  const [existeLogAcbr, setExisteLogAcbr] = useState<LogFilesProps>({
    logFound: false,
    logs: [],
  });
  const [nfManutencao, setNfManutencao] = useState<boolean>(false);
  const [nfCancelada, setNfCancelada] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [qtdAgendamento, setQtdAgendamento] = useState({
    pagar: 0,
    receber: 0,
  });
  const [agendamentos, setAgendamentos] = useState<AgendamentosProps[]>([]);
  const [tipoOperacao, setTipoOperacao] = useState<number | undefined>(
    undefined,
  );
  const [localConsumoDesabilitado, setLocalConsumoDesabilitado] =
    useState(false);
  const [codCidadeEntregaRetriever, setCodCidadeEntregaRetriever] = useState<
    number | undefined
  >(undefined);

  const masterDetailDefault = [
    {
      obj_name: 'produtos',
      pk_fields: ['cod_seq_produto'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
    {
      obj_name: 'financeiro',
      pk_fields: ['cod_seq_parcela'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
  ];

  const [masterDetail, setMasterDetail] =
    useState<MasterDetailProps[]>(masterDetailDefault);

  const [isModalOpen, setIsModalOpen] = useState<any>({
    isOpen: false,
    modal: null,
    data: undefined,
  });
  const [produtosTable, setProdutosTable] = useState<any[]>([]);
  const [finalizadorasTable, setFinalizadorasTable] = useState<any[]>([]);
  const [isOpenModalFunction, setIsOpenModalFunction] = useState(false);
  const [pedidosPendentes, setPedidosPendentes] = useState<any[]>([]);
  const [showTablePedidosPendentes, setShowTablePedidosPendentes] = useState({
    pedido: false,
    item: false,
  });
  const [pedido, setPedido] = useState<any[]>([]);

  const [pedidoPendenteSelected, setPedidoPententeSelected] = useState<
    any | null
  >([]);

  const [itensPedidoPendente, setItensPedidoPendente] = useState<any[]>([]);

  const [finalizadorasPedido, setFinalizadorasPedido] = useState<any[]>([]);
  const handlePedidosPendentes = useCallback((pedidoPendente: any[]) => {
    setPedidosPendentes(pedidoPendente);
  }, []);

  const handleItensPedidosPendentes = useCallback((newItems: any[]) => {
    setItensPedidoPendente(newItems);
  }, []);

  const handlePedidoPendenteSelected = useCallback((novoEstado: any) => {
    setPedidoPententeSelected((prevSelected: any[] = []) => [
      ...prevSelected,
      novoEstado,
    ]);
  }, []);
  const handleShowTablePedidosPendentes = useCallback(
    (showPedido: boolean, showItem: boolean) => {
      setShowTablePedidosPendentes({ pedido: showPedido, item: showItem });
    },
    [],
  );
  const onCloseModal = useCallback(() => {
    setIsModalOpen({ isOpen: false, modal: null, data: undefined });
  }, []);

  const onOpenModal = useCallback((modal: any, data?: any) => {
    setIsModalOpen({ isOpen: true, modal, data });
  }, []);

  const handleAddProdutoTable = async (produto: any) => {
    if (Array.isArray(produto)) {
      produto.forEach(async (element) => {
        const produtosDetail: any[] = await insertOrUpdate(
          'produtos',
          element as any,
          masterDetail,
          setMasterDetail,
        );
        setProdutos(produtosDetail);
      });
    }
  };

  const handleAddFinalizadoraTable = useCallback(
    (finalizadora: any | any[]) => {
      if (Array.isArray(finalizadora)) {
        setFinalizadorasTable(finalizadora);
        return;
      }

      setFinalizadorasTable((prev) => [...prev, finalizadora]);
    },
    [],
  );

  const handleAddFinalizadoraPedido = useCallback(
    (finalizadoraItem: any | any[]) => {
      if (Array.isArray(finalizadoraItem)) {
        setFinalizadorasPedido(finalizadoraItem);
        return;
      }

      setFinalizadorasPedido((prev) => [...prev, finalizadoraItem]);
    },
    [],
  );

  const handlePedido = useCallback((item: any | any[]) => {
    if (Array.isArray(item)) {
      setPedido(item);
      return;
    }
    setPedido((prev) => [...prev, item]);
  }, []);

  const handleChangeOpenModalFunction = useCallback((isOpen: boolean) => {
    setIsOpenModalFunction(isOpen);
  }, []);

  const {
    register: registerImportarPedido,
    control: controlImportarPedido,
    setValue: setValueImportarPedido,
    getValues: getValuesImportarPedido,
    setError: setErrorImportarPedido,
    clearErrors: clearErrorsImportarPedido,
    formState: formStateImportarPedido,
    resetField: resetFieldImportarPedido,
  } = useForm();

  const foirmImportarPedido = useMemo(() => {
    return {
      registerImportarPedido,
      controlImportarPedido,
      setValueImportarPedido,
      getValuesImportarPedido,
      setErrorImportarPedido,
      clearErrorsImportarPedido,
      formStateImportarPedido,
    };
  }, [
    clearErrorsImportarPedido,
    controlImportarPedido,
    formStateImportarPedido,
    getValuesImportarPedido,
    registerImportarPedido,
    setErrorImportarPedido,
    setValueImportarPedido,
  ]);

  const {
    register: registerTabFinalizacao,
    control: controlTabFinalizacao,
    setValue: setValueTabFinalizacao,
    getValues: getValuesTabFinalizacao,
    reset: resetTabFinalizacao,
    watch: watchTabFinalizacao,
    resetField: resetFieldTabFinalizacao,
    formState: formStateTabFinalizacao,
  } = useForm();

  const formTabFinalizacao = useMemo(() => {
    return {
      registerTabFinalizacao,
      controlTabFinalizacao,
      setValueTabFinalizacao,
      getValuesTabFinalizacao,
      resetTabFinalizacao,
      watchTabFinalizacao,
      resetFieldTabFinalizacao,
      formStateTabFinalizacao,
    };
  }, [
    registerTabFinalizacao,
    controlTabFinalizacao,
    setValueTabFinalizacao,
    getValuesTabFinalizacao,
    resetTabFinalizacao,
    watchTabFinalizacao,
    resetFieldTabFinalizacao,
    formStateTabFinalizacao,
  ]);

  function changeLoja(data: any) {
    setLoja(data);
  }
  function changeParceiro(data: any) {
    setParceiro(data);
  }

  /**
   * Form NFE
   */
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    setFocus,
    clearErrors,
    formState,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });
  /**
   * Form Itens
   */
  const {
    register: registerFormItens,
    handleSubmit: handleSubmitFormItens,
    control: controlFormItens,
    setValue: setValueFormItens,
    getValues: getValuesFormItens,
    setError: setErrorFormItens,
    setFocus: setFocusFormItens,
    clearErrors: clearErrorsFormItens,
    formState: formStateFormItens,
    watch: watchFormItens,
    reset: resetFormItens,
  } = useForm({
    resolver: yupResolver(schemaFormItens),
    reValidateMode: 'onBlur',
  });
  /**
   * Form Financeiro
   */
  const {
    register: registerFormFinanceiro,
    handleSubmit: handleSubmitFormFinanceiro,
    control: controlFormFinanceiro,
    reset: resetFormFinanceiro,
    setValue: setValueFormFinanceiro,
    getValues: getValuesFormFinanceiro,
    setFocus: setFocusFormFinanceiro,
    formState: formStateFinanceiro,
    watch: watchFormFinanceiro,
    setError: setErrorFormFinanceiro,
    clearErrors: clearErrorsFormFinanceiro,
  } = useForm({
    resolver: yupResolver(schemaFormFinanceiro),
    reValidateMode: 'onChange',
  });
  /**
   * Form Dados Entrega
   */
  const {
    register: registerDadosEntrega,
    handleSubmit: handleSubmitDadosEntrega,
    control: controlDadosEntrega,
    reset: resetDadosEntrega,
    setValue: setValueDadosEntrega,
    getValues: getValuesDadosEntrega,
    setFocus: setFocusDadosEntrega,
    formState: formStateDadosEntrega,
    watch: watchDadosEntrega,
    setError: setErrorDadosEntrega,
    clearErrors: clearErrorsDadosEntrega,
  } = useForm({
    reValidateMode: 'onChange',
  });

  /**
   * Calcula SubTotais
   */
  useEffect(() => {
    // Recalcula subtotais
    const flg_orgao_publico = getValues('busca_parceiro')?.flg_orgao_publico;
    const flg_calcula_st = getValues('cod_perfil')?.flg_calcula_st;

    const subtotais = CalculaTotais(
      produtos,
      flg_orgao_publico,
      flg_calcula_st,
      loja.tipo_regime,
    );

    // Atualiza state de subtotais e produtos
    setSubTotais(subtotais || 0);
  }, [getValues, loja.tipo_regime, produtos]);

  const resetLoja = useCallback(() => {
    if (user.loja_data) {
      setLoja({
        uf: user.loja_data.des_uf,
        cod_loja: user.loja_data.cod_loja,
        tipo_regime: user.loja_data.tipo_regime,
        cod_cidade: toNumberOrZero(user.cod_cidade),
        empresa: {
          flg_envia_ibs_cbs: user.empresa?.flg_envia_ibs_cbs || false,
          per_cbs_teste: toNumberOrZero(user.empresa?.per_cbs_teste),
          per_ibs_mun_teste: toNumberOrZero(user.empresa?.per_ibs_mun_teste),
          per_ibs_uf_teste: toNumberOrZero(user.empresa?.per_ibs_uf_teste),
        },
      });
      setCodLoja(user.loja_data.cod_loja);
      setValue('loja', user.loja_data.cod_loja);
    }
  }, [
    setValue,
    user.cod_cidade,
    user.empresa?.flg_envia_ibs_cbs,
    user.loja_data,
    user.empresa?.per_cbs_teste,
    user.empresa?.per_ibs_mun_teste,
    user.empresa?.per_ibs_uf_teste,
  ]);

  useEffect(() => {
    resetLoja();
  }, [user, resetLoja]);

  const resetFormData = useCallback(() => {
    if (user.loja_data) {
      setLoja({
        uf: user.loja_data.des_uf,
        cod_loja: user.loja_data.cod_loja,
        tipo_regime: user.loja_data.tipo_regime,
        cod_cidade: toNumberOrZero(user.cod_cidade),
        empresa: {
          flg_envia_ibs_cbs: user.empresa?.flg_envia_ibs_cbs || false,
          per_cbs_teste: toNumberOrZero(user.empresa?.per_cbs_teste),
          per_ibs_mun_teste: toNumberOrZero(user.empresa?.per_ibs_mun_teste),
          per_ibs_uf_teste: toNumberOrZero(user.empresa?.per_ibs_uf_teste),
        },
      });
      setCodLoja(user.loja_data.cod_loja);
    } else {
      setLoja({} as Loja);
      setCodLoja(0);
    }
    setMasterDetail(JSON.parse(JSON.stringify(masterDetailDefault)));
    setProdutos([]);
    setParceiro({} as any);
    setParcelasFinanceiro([]);
    setCuponsReferenciados([]);
    setLogs([]);
    setParametrosLoja({} as ParametrosLoja);
    setParcelaSelecionada({} as ParcelaFinanceiro);
    setProdutoSelecionado({} as Produto);
    setSubTotais({
      totalNF: 0,
      totalBC: 0,
      totalICMS: 0,
      totBcIbsCbs: 0,
      totCbs: 0,
      totIbsEst: 0,
      totIbsMun: 0,
    });
    setCurrentTab('nfe');
    resetFormFinanceiro({
      num_nf: '',
      dta_emissao: '',
      dta_entrada: '',
      num_condicao: 30,
      condicao: {
        value: 2,
        label: 'DD - DIAS DA DATA',
        cod_condicao: 2,
        des_condicao: 'DIAS DA DATA',
        des_definicao: 'DD',
      },
      finalizadora: '',
      dta_vencimento: '',
      val_financeiro: '',
      conta: '',
      categoria: '',
    });
    resetDadosEntrega({
      num_cep_entrega: '',
      des_endereco_entrega: '',
      num_endereco_entrega: '',
      des_bairro_entrega: '',
      des_complemento_entrega: '',
      cod_cidade_entrega: { value: undefined, label: undefined },
    });
    setIsBonificado(false);
    setProdutoSelecionado({} as Produto);
    setIsFormItendEditing({ isEdit: false, uuid: null });
    resetFormItens();
    reset(nfe);
    resetLoja();
    setNfManutencao(false);
    setNfCancelada(false);
    setProdutosTable([]);
    setFinalizadorasTable([]);
    setIsOpenModalFunction(false);
    setPedidosPendentes([]);
    setShowTablePedidosPendentes({
      pedido: false,
      item: false,
    });
    setPedido([]);
    setPedidoPententeSelected([]);
    setItensPedidoPendente([]);
    setFinalizadorasPedido([]);
    setFinalizadorasPedido([]);
    setQtdAgendamento({ pagar: 0, receber: 0 });
    setTipoOperacao(undefined);
    setAgendamentos([]);
    AgendamentoSelecionadosStorage.clearAll(47);
    setLocalConsumoDesabilitado(false);
    setTimeout(() => {
      setValueFormItens('cod_ibs_cbs', { value: undefined, label: undefined });
      setValue('tipo_local_consumo', { value: 0, label: 'ORIGEM' });
      setCodIbsCbsRetriever(null);
    }, 80);
  }, [
    reset,
    resetFormFinanceiro,
    resetFormItens,
    resetLoja,
    user.loja_data,
    masterDetail,
  ]);

  const perfil = watch('cod_perfil');

  useEffect(() => {
    (async () => {
      if (typeof perfil === 'object' && codLoja > 0 && !isUpdate) {
        const { data } = await api.get(
          `/perfil-loja/${codLoja}/${perfil.cod_perfil}/detalhes`,
          {
            params: {
              cod_loja: codLoja,
              cod_perfil: perfil.cod_perfil,
            },
          },
        );
        if (data.data && data.data.length > 0) {
          const result = data.data[0];

          setValueFormFinanceiro('categoria', {
            value: result.cod_subcategoria,
            label: result.descricao,
          });
          setValueFormFinanceiro('conta', {
            value: result.cod_cc,
            label: result.descricao_cc,
          });
        }
      }
    })();
  }, [codLoja, isUpdate, perfil, setValueFormFinanceiro]);

  return (
    <EmissaoNFEContext.Provider
      value={{
        loja,
        changeLoja,
        codLoja,
        setCodLoja,
        parceiro,
        changeParceiro,
        currentTab,
        setCurrentTab,
        cuponsReferenciados,
        setCuponsReferenciados,
        parametrosLoja,
        setParametrosLoja,
        formNFE: {
          watch,
          reset,
          control,
          register,
          setValue,
          setError,
          getValues,
          setFocus,
          formState,
          clearErrors,
          handleSubmit,
        },
        formItens: {
          register: registerFormItens,
          handleSubmit: handleSubmitFormItens,
          control: controlFormItens,
          setValue: setValueFormItens,
          getValues: getValuesFormItens,
          setError: setErrorFormItens,
          clearErrors: clearErrorsFormItens,
          setFocus: setFocusFormItens,
          formState: formStateFormItens,
          watch: watchFormItens,
          reset: resetFormItens,
        },
        // FINANCEIRO
        parcelasFinanceiro,
        setParcelasFinanceiro,
        setIsFormFinanceiroEditing,
        isFormFinanceiroEditing,
        formFinanceiro: {
          register: registerFormFinanceiro,
          handleSubmit: handleSubmitFormFinanceiro,
          control: controlFormFinanceiro,
          reset: resetFormFinanceiro,
          setValue: setValueFormFinanceiro,
          getValues: getValuesFormFinanceiro,
          setFocus: setFocusFormFinanceiro,
          formState: formStateFinanceiro,
          watch: watchFormFinanceiro,
          setError: setErrorFormFinanceiro,
          clearErrors: clearErrorsFormFinanceiro,
        },
        // DADOS ENTREGA
        formDadosEntrega: {
          register: registerDadosEntrega,
          handleSubmit: handleSubmitDadosEntrega,
          control: controlDadosEntrega,
          reset: resetDadosEntrega,
          setValue: setValueDadosEntrega,
          getValues: getValuesDadosEntrega,
          setFocus: setFocusDadosEntrega,
          formState: formStateDadosEntrega,
          watch: watchDadosEntrega,
          setError: setErrorDadosEntrega,
          clearErrors: clearErrorsDadosEntrega,
        },
        produtos,
        setProdutos,
        produtoSelecionado,
        setProdutoSelecionado,
        subTotais,
        setSubTotais,
        isUpdate,
        setIsUpdate,
        parcelaSelecionada,
        setParcelaSelecionada,
        logs,
        setLogs,
        isBonificado,
        setIsBonificado,
        totalFinanceiro,
        setTotalFinanceiro,
        resetFormData,
        isFormItendEditing,
        setIsFormItendEditing,
        listaDeCfop,
        setListaDeCfop,
        existeLogAcbr,
        setExisteLogAcbr,
        masterDetail,
        setMasterDetail,
        nfManutencao,
        setNfManutencao,
        setNfCancelada,
        nfCancelada,
        isDisabled,
        setIsDisabled,

        // modal pedido
        isModalOpen,
        onCloseModal,
        handleChangeOpenModalFunction,
        produtosTable,
        finalizadorasTable,
        handlePedidosPendentes,
        handleShowTablePedidosPendentes,
        showTablePedidosPendentes,
        pedidoPendenteSelected,
        handleAddProdutoTable,
        handlePedido,
        handleAddFinalizadoraTable,
        handleAddFinalizadoraPedido,
        foirmImportarPedido,
        formTabFinalizacao,
        onOpenModal,
        pedidosPendentes,
        itensPedidoPendente,
        handleItensPedidosPendentes,
        handlePedidoPendenteSelected,
        pedido,
        setItensPedidoPendente,
        setPedidoPententeSelected,

        qtdAgendamento,
        setQtdAgendamento,
        agendamentos,
        setAgendamentos,
        tipoOperacao,
        setTipoOperacao,
        localConsumoDesabilitado,
        setLocalConsumoDesabilitado,

        codCidadeEntregaRetriever,
        setCodCidadeEntregaRetriever,
        codIbsCbsRetriever,
        setCodIbsCbsRetriever,
      }}
    >
      {children}
    </EmissaoNFEContext.Provider>
  );
}

export const useEmissaoNFE = (): EmissaoNFEContextData => {
  return useContext(EmissaoNFEContext);
};
